let _ = t => t,
  _t;
import { useState } from 'react';
import { usePagination } from '../../pagination/paginationHooks';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { H3 } from 'foundations-components/transitional/components/H3';
import { CollapsedButtonsGroup } from '../../buttons/CollapsedButtonsGroup';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import styledWithTheme from 'foundations-theming/utils';
import { LearningContentCard } from 'academy-common-lib/features/learningContentCard/components/LearningContentCard';
import { LearningContentCardsSkeleton } from 'academy-common-lib/components/Skeletons/LearningContentCardsSkeleton';
import NoResultsError from '../../../components/Errors/NoResultsError';
import ApiError from '../../../components/Errors/ApiError';
import { ACADEMY_ONBOARDING_URL_FRAGMENT } from '../../../constants/Navigation';
import { Button } from 'foundations-components/transitional/components/Button';
import { useOnboardingResources } from '../resourcesQueries';
import OnboardingSectionLayout from './OnboardingSectionLayout';
import { FiltersBar } from '../../filters/components/FiltersCard';
import { LanguageFilter } from '../../filters/components/LanguageFilter';
import { ProductFilter } from './ProductFilter';
import { FILTER_TYPES } from '../../../constants/TopFilterConstants';
// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../../../hooks/searchParamHooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TAB_OPTIONS = [{
  message: 'learningCenter.academyOnboarding.resources.tabAll',
  value: 'ALL'
}, {
  message: 'learningCenter.academyOnboarding.resources.tabPlaylists',
  value: 'PLAYLISTS'
}, {
  message: 'learningCenter.academyOnboarding.resources.tabPrepGuides',
  value: 'PREP_GUIDES'
}, {
  message: 'learningCenter.academyOnboarding.resources.tabRecordings',
  value: 'RECORDINGS'
}, {
  message: 'learningCenter.academyOnboarding.resources.tabWorkbooks',
  value: 'WORKBOOKS'
}];
const TAB_CONTENT_TYPES = {
  ALL: undefined,
  PLAYLISTS: ['LEARNING_PATH'],
  PREP_GUIDES: ['LESSON'],
  RECORDINGS: ['SHORT_FORM_CONTENT'],
  WORKBOOKS: ['LESSON']
};
const LARGE_PAGE_SIZE = 12;
const SMALL_PAGE_SIZE = 8;
const ResourcesGrid = styledWithTheme.div(_t || (_t = _`
  display: grid;
  grid-template-columns: ${0};
  gap: ${0};
  width: 100%;
`), ({
  $isMobile
}) => $isMobile ? '1fr' : 'repeat(auto-fill, minmax(340px, 1fr))', ({
  $isMobile,
  theme
}) => $isMobile ? theme.space['200'] : theme.space['600']);
const ResourcesSection = () => {
  const {
    isMobile,
    isDesktop
  } = useMediaQuery();
  const [activeTab, setActiveTab] = useState('ALL');
  const {
    currentPage,
    setPage
  } = usePagination();
  const [productGroups] = useSearchParam(FILTER_TYPES.TOOLS);
  const [[language]] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const pageSize = isDesktop ? LARGE_PAGE_SIZE : SMALL_PAGE_SIZE;
  const contentTypes = TAB_CONTENT_TYPES[activeTab];
  const {
    resources,
    loading,
    error,
    totalCount
  } = useOnboardingResources({
    contentTypes,
    productGroups,
    language,
    pageLength: pageSize,
    offset: (currentPage - 1) * pageSize
  });
  const totalPages = Math.ceil(totalCount / pageSize);
  const handleTabChange = tab => {
    setActiveTab(tab);
    setPage(1);
  };
  const handleResetFilters = () => {
    setActiveTab('ALL');
    setPage(1);
  };
  const handlePageChange = evt => {
    setPage(evt.target.value);
  };
  const isEmpty = !loading && !error && (!resources || resources.length === 0);
  return /*#__PURE__*/_jsxs(OnboardingSectionLayout, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      alignItems: isMobile ? 'stretch' : 'center',
      flexDirection: isMobile ? 'column' : 'row',
      gap: "400",
      justifyContent: "space-between",
      marginBlockEnd: "600",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flex: "1",
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(H3, {
          typography: "heading-300",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.academyOnboarding.resources.title"
          })
        }), /*#__PURE__*/_jsx(Text, {
          typography: "body-100",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.academyOnboarding.resources.description"
          })
        })]
      }), /*#__PURE__*/_jsxs(FiltersBar, {
        shouldRenderClearFilters: false,
        children: [/*#__PURE__*/_jsx(ProductFilter, {
          appearsIn: "academy-onboarding-resources"
        }), /*#__PURE__*/_jsx(LanguageFilter, {
          appearsIn: "academy-onboarding-resources"
        })]
      })]
    }), /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "column",
      gap: "800",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(CardWrapper, {
          marginBlockEnd: "600",
          padding: "400",
          children: /*#__PURE__*/_jsx(CollapsedButtonsGroup, {
            alignItems: "center",
            children: TAB_OPTIONS.map(option => /*#__PURE__*/_jsx(Button, {
              active: activeTab === option.value,
              onClick: () => handleTabChange(option.value),
              use: "tertiary-extra-light",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: option.message
              })
            }, option.value))
          })
        }), loading ? /*#__PURE__*/_jsx(ResourcesGrid, {
          $isMobile: isMobile,
          children: /*#__PURE__*/_jsx(LearningContentCardsSkeleton, {
            maxSkeletonListLength: pageSize,
            skeletonCardSize: "small",
            use: "on-light"
          })
        }) : error ? /*#__PURE__*/_jsx(ApiError, {
          error: error,
          markerDefault: "academy-onboarding-resources-error",
          resetLink: ACADEMY_ONBOARDING_URL_FRAGMENT
        }) : isEmpty ? /*#__PURE__*/_jsx(NoResultsError, {
          onReset: handleResetFilters,
          resetLink: ACADEMY_ONBOARDING_URL_FRAGMENT
        }) : /*#__PURE__*/_jsx(ResourcesGrid, {
          $isMobile: isMobile,
          children: resources.map(resource => /*#__PURE__*/_jsx(LearningContentCard, {
            item: resource,
            size: "small"
          }, resource.id))
        })]
      }), totalPages > 1 && /*#__PURE__*/_jsx(Paginator, {
        onPageChange: handlePageChange,
        page: currentPage,
        pageCount: totalPages,
        showFirstLastButtons: true
      })]
    })]
  });
};
export default ResourcesSection;